(() => {
  'use strict';

  const root = window;
  const document = root.document;
  const KEY = '__luggexCartCheckoutUX';
  if (root[KEY]) { root[KEY].scan(); return; }
  const ROOT = '[data-luggex-cart-checkout-root]';
  const ACTIONS = '[data-luggex-cart-checkout-actions]';
  const NORMAL = 'a.xcotton-checkout-without';
  const PAID = 'button.xcotton-checkout[data-xmh-checkout="true"]';
  const states = new Map();
  const unloaded = new WeakSet();
  let queued;
  let wake;
  let observing = false;
  let alive = true;
  let suspended = false;

  const owned = (element, scope) => element?.closest(ROOT) === scope;
  const branchWithin = (element, parent) => {
    let branch = element;
    while (branch?.parentNode && branch.parentNode !== parent) branch = branch.parentNode;
    return branch?.parentNode === parent ? branch : null;
  };
  const originalSubmit = (element) => {
    if (!element || element.tagName !== 'BUTTON' && element.tagName !== 'INPUT' || element.getAttribute('name') !== 'checkout' || element.getAttribute('type') !== 'submit') return false;
    return element.hidden || element.style?.display === 'none' || root.getComputedStyle?.(element).display === 'none';
  };
  const busyControl = (button) => button.disabled || button.hasAttribute('disabled') || button.getAttribute('aria-busy') === 'true' || button.getAttribute('data-state') === 'loading' || button.classList.contains('loading');
  const busy = (actions) => [...actions.querySelectorAll(`${NORMAL}, ${PAID}, [name="checkout"]`)].some(busyControl);
  const labelNormal = (normal, label) => {
    if (!label) return;
    const holder = normal.querySelector('[data-luggex-cart-checkout-normal-label], .btn-text, .button-text') || normal;
    const textNodes = [];
    const collect = (node) => {
      if (node.nodeType === 3) { if (node.nodeValue.trim()) textNodes.push(node); return; }
      if (node.nodeType !== 1 || ['SVG', 'SCRIPT', 'STYLE'].includes(node.tagName.toUpperCase()) || node.getAttribute('aria-hidden') === 'true') return;
      [...node.childNodes].forEach(collect);
    };
    collect(holder);
    if (!textNodes.length) { holder.appendChild(document.createTextNode(label)); return; }
    if (textNodes[0].nodeValue !== label) textNodes[0].nodeValue = label;
    textNodes.slice(1).forEach((node) => { if (node.nodeValue) node.nodeValue = ''; });
  };
  const repair = (scope, state) => {
    if (!scope.isConnected || unloaded.has(scope) || Date.now() < state.checkoutUntil) return;
    const actionCandidates = [...scope.querySelectorAll(ACTIONS)].filter((actions) => owned(actions, scope));
    if (actionCandidates.length !== 1) return;
    const actions = actionCandidates[0];
    if (state.actions !== actions) { state.actions = actions; state.repairs = []; state.loopPaused = false; }
    if (state.loopPaused) return;
    if (busy(actions)) return;

    let moved = false;
    // The warranty belongs before this root's CTA. Its initial main-cart
    // markup lives just outside the sidebar, in the same sticky host.
    const warranty = scope.querySelector('#LuggexCartLifetime') || scope.closest('sticky-element')?.querySelector('#LuggexCartLifetime');
    const actionBranch = branchWithin(actions, scope);
    if (warranty && actionBranch && (!warranty.closest(ROOT) || owned(warranty, scope))) {
      if (warranty.parentNode !== scope || warranty.nextElementSibling !== actionBranch) { scope.insertBefore(warranty, actionBranch); moved = true; }
      if (!warranty.classList.contains('is-ready')) warranty.classList.add('is-ready');
    }

    const normalCandidates = [...actions.querySelectorAll(NORMAL)].filter((normal) => owned(normal, scope));
    const paidCandidates = [...actions.querySelectorAll(PAID)].filter((paid) => owned(paid, scope));
    if (normalCandidates.length === 1 && !normalCandidates[0].hasAttribute('href')) {
      const normal = normalCandidates[0];
      if (!normal.hasAttribute('role')) normal.setAttribute('role', 'button');
      if (!normal.hasAttribute('tabindex')) normal.setAttribute('tabindex', '0');
    }
    if (normalCandidates.length === 1 && paidCandidates.length === 1) {
      const normal = normalCandidates[0], paid = paidCandidates[0];
      if (normal.parentNode === paid.parentNode) {
        // Keep either observed paid/native adjacency direction. The provider's
        // real paid clone currently precedes its hidden original submit.
        const next = paid.nextElementSibling === normal ? normal.nextElementSibling : paid.nextElementSibling;
        const last = originalSubmit(next) ? next : paid;
        if (last.nextElementSibling !== normal) { normal.parentNode.insertBefore(normal, last.nextSibling); moved = true; }
        labelNormal(normal, scope.getAttribute('data-luggex-cart-checkout-label'));
      }
    }

    for (const wrapper of scope.querySelectorAll('#xcottonWrapper')) {
      if (!owned(wrapper, scope)) continue;
      const parent = wrapper.parentNode;
      if ([...parent.querySelectorAll('#xcottonWrapper')].filter((candidate) => candidate.parentNode === parent).length !== 1) continue;
      const branch = branchWithin(actions, parent);
      // Do not adopt a wrapper into a new parent or into the CTA group.
      if (!branch || branch === wrapper || parent === actions || actions.contains(parent)) continue;
      if (wrapper.previousElementSibling !== branch) { parent.insertBefore(wrapper, branch.nextSibling); moved = true; }
    }
    if (moved) {
      state.repairs = state.repairs.filter((time) => Date.now() - time < 6000);
      state.repairs.push(Date.now());
      if (state.repairs.length >= 6) state.loopPaused = true;
    }
  };
  const normalize = () => {
    queued = undefined;
    if (!alive || suspended) return;
    states.forEach((state, scope) => { if (!scope.isConnected) states.delete(scope); });
    for (const scope of document.querySelectorAll(ROOT)) {
      if (unloaded.has(scope)) continue;
      let state = states.get(scope);
      if (!state) { state = { repairs: [], checkoutUntil: 0 }; states.set(scope, state); }
      repair(scope, state);
    }
    // Moves/text updates above are idempotent. Discard only this synchronous
    // batch's records; later plugin rebuilds remain observed.
    observer.takeRecords();
  };
  const scan = () => {
    if (alive && !suspended && queued === undefined) queued = root.setTimeout(normalize, 0);
  };
  const reset = () => {
    states.forEach((state) => { state.loopPaused = false; state.repairs = []; });
    scan();
  };
  const checkoutStart = (event) => {
    if (!alive) return;
    const scope = event.target?.closest?.(ROOT);
    for (const candidate of document.querySelectorAll(ROOT)) {
      if (scope && candidate !== scope) continue;
      let state = states.get(candidate);
      if (!state) { state = { repairs: [] }; states.set(candidate, state); }
      state.checkoutUntil = Date.now() + 10000;
    }
    root.clearTimeout(wake);
    wake = root.setTimeout(scan, 10010);
  };
  const watchClick = (event) => {
    const control = event.target?.closest?.(`${NORMAL}, ${PAID}, [name="checkout"], [data-luggex-native-checkout-fallback]`);
    if (control?.closest(ROOT)) checkoutStart({ target: control });
  };
  const watchKeys = (event) => {
    if (!alive || suspended || event.defaultPrevented || event.repeat || event.isComposing || event.key !== 'Enter' && event.key !== ' ') return;
    const normal = event.target?.closest?.(NORMAL);
    const scope = normal?.closest(ROOT);
    const actions = normal?.closest(ACTIONS);
    if (!normal || normal.hasAttribute('href') || !scope?.isConnected || unloaded.has(scope) || !actions || !owned(actions, scope) || normal.getAttribute('aria-disabled') === 'true' || busyControl(normal)) return;
    // A provider anchor without href has no native button keyboard action.
    // Cancel only this handled key's default, then use its existing click API.
    event.preventDefault();
    normal.click();
  };
  const checkoutAborted = () => { states.forEach((state) => { state.checkoutUntil = 0; }); reset(); };
  const sectionLoad = (event) => {
    if (event.target?.matches?.(ROOT)) unloaded.delete(event.target);
    event.target?.querySelectorAll?.(ROOT).forEach((scope) => unloaded.delete(scope));
    reset();
  };
  const sectionUnload = (event) => {
    if (event.target?.matches?.(ROOT)) { unloaded.add(event.target); states.delete(event.target); }
    event.target?.querySelectorAll?.(ROOT).forEach((scope) => { unloaded.add(scope); states.delete(scope); });
  };
  const observer = new MutationObserver((records) => {
    if (records.some((record) => (record.target?.nodeType === 3 ? record.target.parentElement : record.target)?.closest?.(ROOT) || [...(record.addedNodes || []), ...(record.removedNodes || [])].some((node) => node.nodeType === 1 && (node.matches?.(ROOT) || node.querySelector?.(ROOT))))) scan();
  });
  const observe = () => {
    if (observing || !document.body || !alive) return;
    observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'disabled', 'aria-busy'] });
    observing = true;
  };
  const pageHide = () => {
    suspended = true;
    root.clearTimeout(queued); queued = undefined;
    root.clearTimeout(wake);
    observer.disconnect(); observing = false;
  };
  const pageShow = () => {
    suspended = false;
    states.forEach((state) => { state.checkoutUntil = 0; state.loopPaused = false; state.repairs = []; });
    observe(); scan();
  };
  const ready = () => { observe(); scan(); };
  const destroy = () => {
    if (!alive) return;
    pageHide(); alive = false;
    document.removeEventListener('DOMContentLoaded', ready);
    document.removeEventListener('cart:updated', reset);
    document.removeEventListener('shopify:section:load', sectionLoad);
    document.removeEventListener('shopify:section:unload', sectionUnload);
    document.removeEventListener('luggex:checkout-start', checkoutStart);
    document.removeEventListener('luggex:checkout-aborted', checkoutAborted);
    document.removeEventListener('click', watchClick, true);
    document.removeEventListener('keydown', watchKeys);
    root.removeEventListener('pagehide', pageHide);
    root.removeEventListener('pageshow', pageShow);
    states.clear();
    if (root[KEY]?.destroy === destroy) delete root[KEY];
  };
  document.addEventListener('DOMContentLoaded', ready, { once: true });
  document.addEventListener('cart:updated', reset);
  document.addEventListener('shopify:section:load', sectionLoad);
  document.addEventListener('shopify:section:unload', sectionUnload);
  document.addEventListener('luggex:checkout-start', checkoutStart);
  document.addEventListener('luggex:checkout-aborted', checkoutAborted);
  document.addEventListener('click', watchClick, { capture: true, passive: true });
  document.addEventListener('keydown', watchKeys);
  root.addEventListener('pagehide', pageHide);
  root.addEventListener('pageshow', pageShow);
  root[KEY] = { scan, destroy };
  ready();
})();
